// Hooks
import { useState, useRef } from 'react';

// Components
import Button from '../Button/Button';
import InputText from '../InputText/InputText';
import InputCheckbox from '../InputCheckbox/InputCheckbox';
import PlaybookPdf from '../PlaybookPdf/PlaybookPdf';
import ShareOptions from '../ShareOptions/ShareOptions';

// Utils
import handleNewsletterSubscribe, { handleEmailCapture } from '../../utils/handleNewsletterSubscribe';
import validateEmail from '../../utils/validateEmail';

const ModalForm = (props) => {
  const { pages } = props;

  const [activePage, setActivePage] = useState(0);
  const modalRef = useRef();
  const formElementsRef = useRef();

  const validateForm = (formElements) => {
    let validated = true;

    [...formElements].forEach((el) => {
      const value = el.value;
      const elementWrapper = el.parentElement;

      // Required
      if (el.dataset.required) {
        if (!value) {
          elementWrapper.classList.add('is-invalid');
          validated = false;
        } else {
          elementWrapper.classList.remove('is-invalid');
        }
      }

      // Validation
      if (el.dataset.validation) {
        if (el.dataset.validation === 'email') {
          if (!validateEmail(value)) {
            elementWrapper.classList.add('is-invalid');
            validated = false;
          } else {
            elementWrapper.classList.remove('is-invalid');
          }
        }
      }
    });
    return validated;
  };

  const goToNextPage = (currentFormElement) => {
    const formElements = formElementsRef.current.querySelectorAll('input');

    if (validateForm(formElements)) {
      setActivePage(activePage + 1);

      if (!currentFormElement.subscribe) {
        return;
      }

      let newsletterSignup = true;
      let email = '';

      [...formElements].forEach((el) => {
        // When there is a newsletter-signup checkbox in the form, see if it is checked

        if (el.name === 'newsletter-signup') {
          if (!el.checked) {
            newsletterSignup = false;
          }
        }
        if (el.type === 'email') {
          email = el.value;
        }
      });

      handleEmailCapture(email);
      if (currentFormElement.onSubmit) {
        currentFormElement.onSubmit(email, newsletterSignup);
      }

      if (!newsletterSignup) {
        return;
      }

      handleNewsletterSubscribe(email);
    }
  };

  const formPages = pages.map((page, pageIndex) => {
    const formPage = [];

    if (page.elements) {
      page.elements.forEach((element, elementIndex) => {
        switch (element.type) {
          case 'title':
            formPage.push(
              <h2 key={`${pageIndex}_${elementIndex}`} className="ModalForm-title">
                {element.label}
              </h2>,
            );
            break;
          case 'subtitle':
            formPage.push(
              <span
                key={`${pageIndex}_${elementIndex}`}
                className="ModalForm-subtitle"
                dangerouslySetInnerHTML={{ __html: element.label }}
              />,
            );
            break;
          case 'illustration':
            formPage.push(
              <div key={`${pageIndex}_${elementIndex}`} className="ModalForm-illustration">
                <svg className={`ModalForm-graphic ${element.size === 'large' ? 'ModalForm-graphic--large' : ''}`}>
                  <use xlinkHref={`#${element.label}`} />
                </svg>
              </div>,
            );
            break;
          case 'text':
            formPage.push(
              <InputText
                key={`${pageIndex}_${elementIndex}`}
                placeholder={element.label}
                validation={element.validation}
                required={element.required}
              />,
            );
            break;
          case 'email':
            formPage.push(
              <InputText
                key={`${pageIndex}_${elementIndex}`}
                placeholder={element.label}
                type="email"
                validation={element.validation}
                required={element.required}
              />,
            );
            break;
          case 'checkbox':
            formPage.push(
              <InputCheckbox key={`${pageIndex}_${elementIndex}`} label={element.label} name={element.name || null} />,
            );
            break;
          case 'button':
            if (element.action === 'pdf-download') {
              formPage.push(
                <PlaybookPdf
                  key={`${pageIndex}_${elementIndex}`}
                  label={element.label}
                  modifier={element.modifier}
                  modal={modalRef}
                />,
              );
            }
            if (element.action === 'next')
              formPage.push(
                <Button
                  key={`${pageIndex}_${elementIndex}`}
                  label={element.label}
                  modifier={element.modifier}
                  onClick={() => goToNextPage(element)}
                />,
              );
            break;
          case 'share-options':
            formPage.push(<ShareOptions key={`${pageIndex}_${elementIndex}`} element={element} />);
            break;
          default:
            break;
        }
      });
    }

    return formPage;
  });

  return (
    <div className="ModalForm" ref={modalRef}>
      <div className="ModalForm-elements" ref={formElementsRef}>
        {formPages &&
          formPages.map((formPage, index) => (
            <div key={index} className="ModalForm-page">
              {activePage === index && formPage}
            </div>
          ))}
      </div>
    </div>
  );
};

export default ModalForm;
